import { Building2, HeartHandshake, LayoutDashboard, Vault } from "lucide-react"
import { useGetBackofficeOrganizationQuery } from "modules/backoffice/hooks/organizations/useGetBackofficeOrganizationQuery"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { Loading } from "modules/shared/components/Loading"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Route as BackofficeOrganizationRoute } from "routes/_base/_auth/backoffice/organizations/$id"
import { BackofficeSection } from "../BackofficeSection"
import { BackofficeCompanySections } from "../companies/BackofficeCompanySections"
import { BackofficeImpersonateOrganization } from "./BackofficeImpersonateOrganization"

export const BackofficeOrganizationDetail = () => {
  const { t } = useTranslation()
  const { id: organizationId } = BackofficeOrganizationRoute.useParams()

  const { data: organization, isPending } = useGetBackofficeOrganizationQuery({
    query: { id: organizationId },
  })

  const isSingleCompany = organization?.companies.length === 1
  const singleCompany = isSingleCompany ? organization.companies[0] : null

  const organizationCards = useMemo(() => {
    return [
      {
        icon: Vault,
        title: t("backoffice.credits.title"),
        url: "/backoffice/organizations/$id/credits/list" as const,
        params: { id: organizationId },
      },
    ]
  }, [organizationId, t])

  const singleCompanyCards = useMemo(() => {
    if (!singleCompany) return []

    return [
      {
        icon: HeartHandshake,
        title: t("backoffice.customers.title"),
        url: "/backoffice/organizations/$id/companies/$companyId/customers/list" as const,
        params: { id: organizationId, companyId: singleCompany.id },
      },
      {
        icon: LayoutDashboard,
        title: t("backoffice.canonicalTopics.title"),
        url: "/backoffice/organizations/$id/companies/$companyId/canonical-topics/list" as const,
        params: { id: organizationId, companyId: singleCompany.id },
      },
    ]
  }, [organizationId, singleCompany, t])

  const companyCards = useMemo(() => {
    if (isSingleCompany) return []
    return (
      organization?.companies.map((company) => ({
        icon: Building2,
        logo: company.logo ?? undefined,
        title: company.name,
        url: "/backoffice/organizations/$id/companies/$companyId" as const,
        params: { id: organizationId, companyId: company.id },
      })) ?? []
    )
  }, [organization?.companies, organizationId, isSingleCompany])

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-40">
        <Loading />
      </div>
    )
  }

  return (
    <>
      <HeaderButtons>
        <BackofficeImpersonateOrganization id={organizationId} />
      </HeaderButtons>
      <h2 className="text-2xl font-semibold">{organization?.name}</h2>

      <div className="flex flex-col gap-8 h-full">
        {singleCompany ? (
          <>
            <BackofficeSection items={[...organizationCards, ...singleCompanyCards]} />
            <BackofficeCompanySections
              organizationId={organizationId}
              companyId={singleCompany.id}
              showCards={false}
            />
          </>
        ) : (
          <>
            <BackofficeSection items={organizationCards} />
            {companyCards.length > 0 && <BackofficeSection items={companyCards} />}
          </>
        )}
      </div>
    </>
  )
}
